import * as THREE from 'three';

/**
 * Procedurally drawn pixel-art tiles (no asset files). Everything uses
 * NearestFilter so texels stay crisp — that's the 16-bit look.
 */

function makeTile(
  size: number,
  draw: (ctx: CanvasRenderingContext2D, size: number) => void
): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  draw(ctx, size);

  const tex = new THREE.CanvasTexture(canvas);
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Deterministic pseudo-random — same speckle every run. */
function rng(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
}

function speckle(
  ctx: CanvasRenderingContext2D,
  size: number,
  colors: string[],
  density: number,
  seed: number
): void {
  const rand = rng(seed);
  const count = Math.floor(size * size * density);
  for (let i = 0; i < count; i++) {
    ctx.fillStyle = colors[Math.floor(rand() * colors.length)];
    ctx.fillRect(Math.floor(rand() * size), Math.floor(rand() * size), 1, 1);
  }
}

export function grassTexture(): THREE.CanvasTexture {
  return makeTile(32, (ctx, s) => {
    ctx.fillStyle = '#4a7c3f';
    ctx.fillRect(0, 0, s, s);
    speckle(ctx, s, ['#3f6e35', '#568c49', '#44753a', '#5d9450'], 0.45, 7);
  });
}

export function dirtTexture(): THREE.CanvasTexture {
  return makeTile(32, (ctx, s) => {
    ctx.fillStyle = '#8b7355';
    ctx.fillRect(0, 0, s, s);
    speckle(ctx, s, ['#7d674c', '#99805f', '#71583f', '#a58a66'], 0.4, 21);
  });
}

export function roadTexture(): THREE.CanvasTexture {
  return makeTile(32, (ctx, s) => {
    ctx.fillStyle = '#3a3a3a';
    ctx.fillRect(0, 0, s, s);
    speckle(ctx, s, ['#333333', '#414141', '#2e2e2e', '#474747'], 0.35, 33);
    // faint tire-polished bands where the wheels run
    ctx.fillStyle = 'rgba(0,0,0,0.12)';
    ctx.fillRect(Math.floor(s * 0.2), 0, Math.floor(s * 0.12), s);
    ctx.fillRect(Math.floor(s * 0.68), 0, Math.floor(s * 0.12), s);
  });
}

export function brickTexture(color: string, mortar = '#4d4238'): THREE.CanvasTexture {
  return makeTile(32, (ctx, s) => {
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, s, s);
    ctx.fillStyle = mortar;
    const rowH = 8;
    for (let y = 0; y < s; y += rowH) {
      ctx.fillRect(0, y, s, 1); // horizontal mortar line
      const offset = (y / rowH) % 2 === 0 ? 0 : 8;
      for (let x = offset; x < s; x += 16) {
        ctx.fillRect(x, y, 1, rowH); // vertical joints, staggered per row
      }
    }
    speckle(ctx, s, ['rgba(0,0,0,0.15)', 'rgba(255,255,255,0.06)'], 0.15, 55);
  });
}

export function checkerTexture(a = '#ffffff', b = '#111111'): THREE.CanvasTexture {
  return makeTile(16, (ctx) => {
    ctx.fillStyle = a;
    ctx.fillRect(0, 0, 16, 16);
    ctx.fillStyle = b;
    ctx.fillRect(0, 0, 8, 8);
    ctx.fillRect(8, 8, 8, 8);
  });
}

export type SkyStops = Array<[number, string]>;

// Synthwave skies — the sun is always setting in the 80s
export const SKY_DAY: SkyStops = [
  [0, '#241454'], [0.35, '#4a2d8f'], [0.6, '#b13d92'], [0.8, '#ff6e5a'], [1, '#ffc36b'],
];
export const SKY_NIGHT: SkyStops = [
  [0, '#050214'], [0.45, '#1a1042'], [0.7, '#3c1a6e'], [0.85, '#7a2a8f'], [1, '#e0447f'],
];
export const SKY_DESERT: SkyStops = [
  [0, '#3a1c6e'], [0.4, '#8a2d7f'], [0.65, '#e05a6b'], [0.85, '#ff9a3a'], [1, '#ffd76b'],
];
export const SKY_SWAMP: SkyStops = [
  [0, '#20303c'], [0.4, '#3a5a54'], [0.7, '#5f8f6e'], [0.85, '#9ab784'], [1, '#d0c98a'],
];

/**
 * Posterized sky: sample a smooth gradient into many small hard steps —
 * visibly banded (16-bit) but with no single harsh boundary.
 */
export function bandedSkyTexture(stops: SkyStops = SKY_DAY, steps = 16): THREE.CanvasTexture {
  const smooth = document.createElement('canvas');
  smooth.width = 1;
  smooth.height = 256;
  const sctx = smooth.getContext('2d')!;
  const gradient = sctx.createLinearGradient(0, 0, 0, 256);
  for (const [at, color] of stops) gradient.addColorStop(at, color);
  sctx.fillStyle = gradient;
  sctx.fillRect(0, 0, 1, 256);
  const pixels = sctx.getImageData(0, 0, 1, 256).data;

  const canvas = document.createElement('canvas');
  canvas.width = 2;
  canvas.height = 256;
  const ctx = canvas.getContext('2d')!;
  const bandH = 256 / steps;
  for (let i = 0; i < steps; i++) {
    const sampleY = Math.min(255, Math.floor((i + 0.5) * bandH));
    const o = sampleY * 4;
    ctx.fillStyle = `rgb(${pixels[o]},${pixels[o + 1]},${pixels[o + 2]})`;
    ctx.fillRect(0, Math.floor(i * bandH), 2, Math.ceil(bandH) + 1);
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.mapping = THREE.EquirectangularReflectionMapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Smooth cinematic sky used by the default renderer profile. */
export function gradientSkyTexture(stops: SkyStops = SKY_DAY): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 4;
  canvas.height = 512;
  const ctx = canvas.getContext('2d')!;
  const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
  for (const [at, color] of stops) gradient.addColorStop(at, color);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const texture = new THREE.CanvasTexture(canvas);
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.mapping = THREE.EquirectangularReflectionMapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

// ---- theme surface tiles ----

export function sandTexture(): THREE.CanvasTexture {
  return makeTile(32, (ctx, s) => {
    ctx.fillStyle = '#d9b678';
    ctx.fillRect(0, 0, s, s);
    speckle(ctx, s, ['#cca96c', '#e3c188', '#c29d60', '#eccd96'], 0.4, 91);
  });
}

export function rockTexture(): THREE.CanvasTexture {
  return makeTile(32, (ctx, s) => {
    ctx.fillStyle = '#a8623e';
    ctx.fillRect(0, 0, s, s);
    speckle(ctx, s, ['#96532f', '#b8724c', '#8a4a28', '#c07d54'], 0.5, 17);
    // horizontal strata lines
    ctx.fillStyle = 'rgba(60,30,15,0.35)';
    for (let y = 3; y < s; y += 7) ctx.fillRect(0, y, s, 1);
  });
}

export function concreteTexture(): THREE.CanvasTexture {
  return makeTile(32, (ctx, s) => {
    ctx.fillStyle = '#5b5b60';
    ctx.fillRect(0, 0, s, s);
    speckle(ctx, s, ['#525257', '#646469', '#4a4a4f', '#6d6d72'], 0.35, 47);
    ctx.fillStyle = 'rgba(0,0,0,0.3)';
    ctx.fillRect(0, 15, s, 1); // panel seam
  });
}

export function murkTexture(): THREE.CanvasTexture {
  return makeTile(32, (ctx, s) => {
    ctx.fillStyle = '#4a5238';
    ctx.fillRect(0, 0, s, s);
    speckle(ctx, s, ['#3f4730', '#565f42', '#333a26', '#5f684a'], 0.5, 73);
  });
}

export function waterTexture(): THREE.CanvasTexture {
  return makeTile(32, (ctx, s) => {
    ctx.fillStyle = '#2c4038';
    ctx.fillRect(0, 0, s, s);
    speckle(ctx, s, ['#243830', '#354a40', '#1e2f28'], 0.3, 29);
    // pale ripple slivers
    const rand = rng(59);
    ctx.fillStyle = 'rgba(160,190,170,0.35)';
    for (let i = 0; i < 6; i++) {
      ctx.fillRect(Math.floor(rand() * s), Math.floor(rand() * s), 3 + Math.floor(rand() * 4), 1);
    }
  });
}

export function darkAsphaltTexture(): THREE.CanvasTexture {
  return makeTile(32, (ctx, s) => {
    ctx.fillStyle = '#2b2b30';
    ctx.fillRect(0, 0, s, s);
    speckle(ctx, s, ['#25252a', '#323237', '#1f1f24'], 0.35, 83);
  });
}

/** Night-city building face: dark facade, random lit windows. */
export function facadeTexture(seed: number): THREE.CanvasTexture {
  return makeTile(32, (ctx, s) => {
    ctx.fillStyle = '#14161d';
    ctx.fillRect(0, 0, s, s);
    const rand = rng(seed);
    const litColors = ['#f5d76e', '#e8ba4a', '#9fd3e8', '#f0e6c8'];
    for (let y = 2; y < s - 2; y += 6) {
      for (let x = 2; x < s - 2; x += 6) {
        if (rand() < 0.42) {
          ctx.fillStyle = litColors[Math.floor(rand() * litColors.length)];
          ctx.fillRect(x, y, 3, 4);
        } else {
          ctx.fillStyle = '#23262e';
          ctx.fillRect(x, y, 3, 4);
        }
      }
    }
  });
}
